import axios from "axios";
import { swrGet, clearSwr } from "./swr";
import { activeCompany } from "./company";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

export const TOKEN_KEY = "aft_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
export const setToken = (t, remember) => { clearToken(); (remember ? localStorage : sessionStorage).setItem(TOKEN_KEY, t); };
export const clearToken = () => { localStorage.removeItem(TOKEN_KEY); sessionStorage.removeItem(TOKEN_KEY); clearSwr(); };
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  const co = activeCompany();
  if (co) cfg.headers["X-Company"] = co;
  if ((cfg.method || "get").toLowerCase() !== "get" && !/\/auth\/track/.test(cfg.url || "")) clearSwr();  // any change invalidates cached page data (page-view tracking is not a change)
  return cfg;
});
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/auth/login")) {
      clearToken();
      window.dispatchEvent(new Event("auth-expired"));
    }
    return Promise.reject(err);
  }
);

const sget = (url, cfg) => swrGet(api, url, cfg);

export const formatApiError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
};

// Auth & users
export const loginRequest = (email, password, remember) => api.post("/auth/login", { email, password, remember }).then(r => r.data);
export const sendWelcome = (id) => api.post(`/auth/users/${id}/send-welcome`).then(r => r.data);
export const checkInvite = (token) => api.get(`/auth/invite/${encodeURIComponent(token)}`).then(r => r.data);
export const acceptInvite = (token, new_password) => api.post("/auth/accept-invite", { token, new_password }).then(r => r.data);
export const fetchMe = () => sget("/auth/me").then(r => r.data);
export const changePassword = (current_password, new_password) => api.post("/auth/change-password", { current_password, new_password }).then(r => r.data);
export const fetchUsers = () => sget("/auth/users").then(r => r.data);
export const createUser = (data) => api.post("/auth/users", data).then(r => r.data);
export const updateUser = (id, data) => api.put(`/auth/users/${id}`, data).then(r => r.data);
export const deleteUser = (id) => api.delete(`/auth/users/${id}`).then(r => r.data);
export const fetchPresets = () => sget("/auth/presets").then(r => r.data);
export const createPreset = (data) => api.post("/auth/presets", data).then(r => r.data);
export const updatePreset = (id, data) => api.put(`/auth/presets/${id}`, data).then(r => r.data);
export const deletePreset = (id) => api.delete(`/auth/presets/${id}`).then(r => r.data);
export const trackPage = (page) => api.post("/auth/track", { page }).catch(() => {});
export const fetchUserActivity = (id) => sget(`/auth/users/${id}/activity`).then(r => r.data);
export const fetchNonDispatchers = () => sget("/analytics/non-dispatchers").then(r => r.data);
export const saveNonDispatchers = (user_ids) => api.put("/analytics/non-dispatchers", { user_ids }).then(r => r.data);
export const fetchOrientLive = (refresh) => sget("/orient/live", { params: refresh ? { refresh: true } : {} }).then(r => r.data);
export const fetchOrientIfta = (week_start) => sget("/orient/ifta", { params: { week_start } }).then(r => r.data);
export const fetchOrientDiscrepancies = (week_start) => sget("/orient/discrepancies", { params: { week_start } }).then(r => r.data);
export const syncSamsaraWeekly = () => api.post("/samsara/sync-weekly").then(r => r.data);

// Dashboard
export const fetchDashboardStats = () => sget("/dashboard/stats").then(r => r.data);

// Shipments
export const fetchOpsOverview = (params) => sget("/ops/overview", { params }).then(r => r.data);
export const fetchOpsLoads = (params) => sget("/ops/loads", { params }).then(r => r.data);
export const fetchOpsFleet = (params) => sget("/ops/fleet", { params }).then(r => r.data);
export const fetchRelayWeekly = () => sget("/ops/relay-weekly").then(r => r.data);
export const fetchOpsAnalytics = (params) => sget("/ops/analytics", { params }).then(r => r.data);
export const fetchShipments = (params) => sget("/shipments", { params }).then(r => r.data);
export const createShipment = (data) => api.post("/shipments", data).then(r => r.data);
export const updateShipment = (id, data) => api.put(`/shipments/${id}`, data).then(r => r.data);
export const deleteShipment = (id) => api.delete(`/shipments/${id}`).then(r => r.data);

// Vehicles
export const fetchVehicles = (params) => sget("/vehicles", { params }).then(r => r.data);
export const createVehicle = (data) => api.post("/vehicles", data).then(r => r.data);
export const updateVehicle = (id, data) => api.put(`/vehicles/${id}`, data).then(r => r.data);
export const deleteVehicle = (id) => api.delete(`/vehicles/${id}`).then(r => r.data);

// Routes
export const fetchRoutes = (params) => sget("/routes", { params }).then(r => r.data);
export const createRoute = (data) => api.post("/routes", data).then(r => r.data);
export const updateRoute = (id, data) => api.put(`/routes/${id}`, data).then(r => r.data);
export const deleteRoute = (id) => api.delete(`/routes/${id}`).then(r => r.data);

// Analytics
export const fetchShipmentVolume = () => sget("/analytics/shipment-volume").then(r => r.data);
export const fetchDeliveryPerformance = () => sget("/analytics/delivery-performance").then(r => r.data);
export const fetchCostBreakdown = () => sget("/analytics/cost-breakdown").then(r => r.data);
export const fetchFleetUtilization = () => sget("/analytics/fleet-utilization").then(r => r.data);
export const fetchLaneAnalytics = (limit) => sget("/analytics/lanes", { params: { limit } }).then(r => r.data);
export const fetchTrucksOnRoad = (week_start, period, exclude_undelivered, full_week, fresh) => (fresh ? api.get : sget)("/analytics/trucks-on-road", { params: { week_start, period, exclude_undelivered, full_week, fresh: fresh || undefined } }).then(r => r.data);
export const fetchUnpaidFuel = (week_start, period, exclude_undelivered, full_week) => sget("/analytics/unpaid-fuel", { params: { week_start, period, exclude_undelivered, full_week } }).then(r => r.data);
export const fetchDriverFuel = (weeks) => sget("/analytics/driver-fuel", { params: { weeks: weeks.join(",") } }).then(r => r.data);
export const fetchCoachHistory = (end_week) => sget("/analytics/driver-coach-history", { params: { end_week } }).then(r => r.data);
export const fetchDataFixes = (week_start) => sget("/data-fixes", { params: { week_start } }).then(r => r.data);
export const fetchDataFixesSummary = () => sget("/data-fixes/summary").then(r => r.data);
export const markDataFixed = (week_start, issue_id, note) => api.post("/data-fixes/mark-fixed", { week_start, issue_id, note }).then(r => r.data);
export const fetchDeadheadSettings = () => sget("/data-fixes/deadhead-settings").then(r => r.data);
export const saveDeadheadSettings = (limit_miles) => api.put("/data-fixes/deadhead-settings", { limit_miles }).then(r => r.data);
export const fetchPartialSettings = () => sget("/data-fixes/partial-settings").then(r => r.data);
export const savePartialSettings = (auto_confirm_saving) => api.put("/data-fixes/partial-settings", { auto_confirm_saving }).then(r => r.data);
export const decidePartial = (week_start, issue_id, decision) => api.post("/data-fixes/partial", { week_start, issue_id, decision }).then(r => r.data);
export const reopenDataFix = (week_start, issue_id) => api.post("/data-fixes/reopen", { week_start, issue_id }).then(r => r.data);
export const fetchSamsaraAccounts = () => sget("/analytics/samsara-driver-accounts").then(r => r.data);
export const fetchTruckMismatch = (week_start) => sget("/analytics/truck-mismatch", { params: { week_start } }).then(r => r.data);
export const fetchDriverScorecard = (period, exclude_undelivered, week_start, fresh) => (fresh ? api.get : sget)("/analytics/driver-scorecard", { params: { period, exclude_undelivered, week_start, fresh: fresh || undefined } }).then(r => r.data);
export const fetchDriverWeekly = (driver_id, period, exclude_undelivered, week_start) => sget("/analytics/driver-scorecard/weekly", { params: { driver_id, period, exclude_undelivered, week_start } }).then(r => r.data);
export const fetchDriverAlerts = (week_start) => sget("/analytics/driver-alerts", { params: { week_start } }).then(r => r.data);
export const fetchDispatcherLeaderboard = (period, exclude_undelivered) => sget("/analytics/dispatcher-leaderboard", { params: { period, exclude_undelivered } }).then(r => r.data);
export const fetchDispatcherDrivers = (id, period, exclude_undelivered) => sget(`/analytics/dispatcher/${id}/drivers`, { params: { period, exclude_undelivered } }).then(r => r.data);
export const fetchFleetHeatmap = (params) => sget("/analytics/fleet-heatmap", { params }).then(r => r.data);
export const fetchFleetHeatmapFilters = () => sget("/analytics/fleet-heatmap/filters").then(r => r.data);
export const fetchDispatcherList = () => sget("/analytics/dispatcher-list").then(r => r.data);
export const fetchDispatcherCompare = (id1, id2, period, exclude_undelivered, id3) => sget("/analytics/dispatcher-compare", { params: { id1, id2, period, exclude_undelivered, id3: id3 || undefined } }).then(r => r.data);
export const fetchLoadHeatmapTime = (period, dispatcher_id, exclude_undelivered) => sget("/analytics/load-heatmap-time", { params: { period, dispatcher_id, exclude_undelivered } }).then(r => r.data);

// Reports & Comparison
export const fetchWeeklyReports = (category) => sget("/reports/weekly", { params: { category } }).then(r => r.data);
export const fetchReportSummary = () => sget("/reports/summary").then(r => r.data);
export const fetchComputedWeekly = (category, week_start_day, exclude_undelivered) => sget("/dashboard/computed-weekly", { params: { category, week_start_day, exclude_undelivered } }).then(r => r.data);
export const fetchAlvysWeeks = (category, week_start_day, exclude_undelivered) => sget("/dashboard/alvys-weeks", { params: { category, week_start_day, exclude_undelivered } }).then(r => r.data);
export const syncHistoricalLoads = () => api.post("/alvys/sync-historical").then(r => r.data);
export const fetchHistoricalSyncStatus = () => sget("/alvys/sync-historical/status").then(r => r.data);
export const uploadComparisonFile = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/comparison/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const fetchUploadedComparison = (category) => sget("/comparison/uploaded", { params: { category } }).then(r => r.data);
export const importReportData = () => api.post("/reports/import").then(r => r.data);

// Alerts
export const fetchAlertThresholds = () => sget("/alerts/thresholds").then(r => r.data);
export const createAlertThreshold = (data) => api.post("/alerts/thresholds", data).then(r => r.data);
export const deleteAlertThreshold = (id) => api.delete(`/alerts/thresholds/${id}`).then(r => r.data);
export const checkAlerts = () => api.get("/alerts/check").then(r => r.data);

// Alvys TMS
export const fetchAlvysStats = () => sget("/alvys/stats").then(r => r.data);
export const fetchAlvysLoads = (params) => sget("/alvys/loads", { params }).then(r => r.data);
export const fetchAlvysTrucks = (params) => sget("/alvys/trucks", { params }).then(r => r.data);
export const fetchAlvysTrailers = (params) => sget("/alvys/trailers", { params }).then(r => r.data);
export const syncAlvysLoads = () => api.post("/alvys/sync-loads").then(r => r.data);
export const fetchAlvysRateAnalytics = () => sget("/alvys/rate-analytics").then(r => r.data);
export const fetchAlvysLoadDetail = (loadNumber) => sget(`/alvys/loads/detail/${loadNumber}`).then(r => r.data);
export const fetchSyncStatus = () => sget("/sync/status").then(r => r.data);

// Samsara Telematics
export const fetchSamsaraFleetSummary = () => sget("/samsara/fleet-summary").then(r => r.data);
export const fetchSamsaraVehicleStats = (limit) => sget("/samsara/vehicle-stats", { params: { limit } }).then(r => r.data);
export const fetchSamsaraFuelReport = (start_date, end_date) => sget("/samsara/fuel-report", { params: { start_date, end_date } }).then(r => r.data);
export const fetchSamsaraDrivers = () => sget("/samsara/drivers").then(r => r.data);
export const fetchSamsaraVehicleUtilization = (weeks_back) => sget("/samsara/vehicle-utilization", { params: { weeks_back } }).then(r => r.data);
export const fetchSamsaraSafetyEvents = (weeks_back) => sget("/samsara/safety-events", { params: { weeks_back } }).then(r => r.data);
export const fetchFuelReport = (period, weeks_back) => sget("/analytics/fuel-report", { params: { period, weeks_back } }).then(r => r.data);

// Relay Fuel
export const fetchRelayFuelTransactions = (weeks_back, company) => sget("/relay/fuel-transactions", { params: { weeks_back, company } }).then(r => r.data);

// Fuel Reconciliation
export const fetchFuelReconciliation = (params, refresh) => sget("/fuel/reconciliation", { params: { ...params, refresh: refresh || undefined } }).then(r => r.data);

// Seed
export const seedData = () => api.post("/seed").then(r => r.data);

export default api;

// Manual fuel entries (receipts)
export const extractFuelReceipts = (files, onUploadProgress) => {
  const form = new FormData();
  files.forEach(f => form.append("files", f));
  return api.post("/fuel/receipts/extract", form, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress, timeout: 180000 }).then(r => r.data);
};
export const createManualFuel = (entry) => api.post("/fuel/manual", entry).then(r => r.data);
export const deleteManualFuel = (id) => api.delete(`/fuel/manual/${id}`).then(r => r.data);
export const fetchReceiptFile = (id) => sget(`/fuel/receipts/${id}/file`, { responseType: "blob" }).then(r => r.data);
export const checkFuelDuplicates = (q) => api.post("/fuel/manual/check-duplicates", q).then(r => r.data);

export const fetchEquipmentTrend = () => sget("/analytics/equipment-trend").then(r => r.data);
export const fetchRankingPresets = () => sget("/analytics/ranking-presets").then(r => r.data);
export const createRankingPreset = (name, weights) => api.post("/analytics/ranking-presets", { name, weights }).then(r => r.data);
export const deleteRankingPreset = (id) => api.delete(`/analytics/ranking-presets/${id}`).then(r => r.data);
export const fetchDispatcherRankingDrivers = (id, equipment) => sget(`/analytics/dispatcher-rankings/${encodeURIComponent(id)}/drivers`, { params: { equipment } }).then(r => r.data);
export const fetchDispatcherRankings = (refresh, poll) => (poll ? api.get : sget)("/analytics/dispatcher-rankings", { params: { refresh: refresh || undefined } }).then(r => r.data);

export const fetchIftaReport = (quarter, company, unit) => sget("/ifta/report", { params: { quarter, company, unit: unit || undefined } }).then(r => r.data);
export const fetchIftaOrientCheck = (quarter, company) => sget("/ifta/orient-check", { params: { quarter, company } }).then(r => r.data);
export const fetchIftaRates = (quarter, refresh) => sget("/ifta/rates", { params: { quarter, refresh: refresh || undefined } }).then(r => r.data);
export const updateIftaRate = (quarter, body) => api.put("/ifta/rates", body, { params: { quarter } }).then(r => r.data);
export const fetchIftaUnits = () => sget("/ifta/units").then(r => r.data);
export const uploadIftaUnits = (company, file) => {
  const fd = new FormData();
  fd.append("company", company);
  fd.append("file", file);
  return api.post("/ifta/units/upload", fd).then(r => r.data);
};
export const fetchIftaFuel = (period, company) => sget("/ifta/fuel", { params: { period, company } }).then(r => r.data);
export const addIftaManualFuel = (body) => api.post("/ifta/fuel/manual", body).then(r => r.data);
export const deleteIftaManualFuel = (id) => api.delete(`/ifta/fuel/manual/${id}`).then(r => r.data);
export const fetchStateSettings = () => sget("/ifta/state-settings").then(r => r.data);
export const saveStateSettings = (state, body) => api.put(`/ifta/state-settings/${state}`, body).then(r => r.data);
export const fetchStateReport = (state, period, company) => sget("/ifta/state-report", { params: { state, period, company } }).then(r => r.data);
export const downloadFile = async (path, params, filename) => {
  const r = await api.get(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
export const fetchIftaUnitHistory = (quarter, all) => sget("/ifta/unit-history", { params: { quarter, all_units: all || undefined } }).then(r => r.data);
export const saveUnitPeriods = (unit, periods) => api.put(`/ifta/units/${encodeURIComponent(unit)}/periods`, { periods }).then(r => r.data);
export const resetUnitPeriods = (unit) => api.delete(`/ifta/units/${encodeURIComponent(unit)}/periods`).then(r => r.data);
export const assignFuelTruck = (id, truck, vin, reason, similar_period) => api.put(`/ifta/fuel/${encodeURIComponent(id)}/truck`, { truck, vin: vin || undefined, reason, similar_period }).then(r => r.data);
export const unassignFuelTruck = (id) => api.delete(`/ifta/fuel/${encodeURIComponent(id)}/truck`).then(r => r.data);
export const fetchWrongTrucks = (quarter, company, refresh) => sget("/ifta/wrong-truck", { params: { quarter, company, refresh: refresh || undefined } }).then(r => r.data);
export const fetchUnitLabels = () => sget("/ifta/unit-labels").then(r => r.data);
export const fetchAliases = () => sget("/ifta/aliases").then(r => r.data);
export const addAlias = (unit, vin, alias) => api.post("/ifta/aliases", { unit, vin, alias }).then(r => r.data);
export const deleteAlias = (alias) => api.delete(`/ifta/aliases/${encodeURIComponent(alias)}`).then(r => r.data);
export const fetchFilings = (params) => sget("/ifta/filings", { params }).then(r => r.data);
export const submitFiling = (body) => api.post("/ifta/filings", body).then(r => r.data);
export const fetchChecklist = (params) => sget("/ifta/filings/checklist", { params }).then(r => r.data);
export const extractFuelStatement = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/fuel/statements/extract", fd, { timeout: 180000 }).then(r => r.data);
};
export const estimateGallons = (body) => api.post("/fuel/estimate-gallons", body).then(r => r.data);
export const fetchStatements = () => sget("/fuel/statements").then(r => r.data);
export const fetchStatement = (id) => sget(`/fuel/statements/${id}`).then(r => r.data);
export const undoStatement = (id, ids) => api.post(`/fuel/statements/${id}/undo`, { ids: ids ?? null }).then(r => r.data);
export const fetchEstimated = (company, status) => sget("/fuel/estimated", { params: { company, status } }).then(r => r.data);
export const reviewEstimate = (id, body) => api.put(`/fuel/manual/${id}/estimate`, body).then(r => r.data);
export const undoEstimateReview = (id) => api.post(`/fuel/manual/${id}/estimate/undo`).then(r => r.data);
export const setAliasLabel = (alias, label) => api.put(`/ifta/aliases/${encodeURIComponent(alias)}/label`, { label }).then(r => r.data);
